/**
 * QUIPWA — NotesStore
 *
 * The notes UI's view of a NotesRepository (lib/notes/notes-repository.ts).
 * It holds what screens render — the list, the open note, search results —
 * and forwards every action to the repository, which answers from the device.
 *
 * Wiring:
 *   DBProvider        → attach(services.notes, { refresh })   the real notes
 *   Design Lab pages  → enableMockMode()                       in-memory notes
 *
 * The repository's change feed (local writes and synced changes) reloads the
 * list, so screens never ask Drive anything. See docs/modulos/notas.md.
 */
import { create } from 'zustand'
import { MockNotesRepository } from '../lib/notes/mock-notes-repository'
import type { Backlink, NoteContent, NoteMetadata, UnlinkedMention } from '../lib/notes/note-model'
import type { NotesRepository } from '../lib/notes/notes-repository'

export type { NoteContent, NoteMetadata } from '../lib/notes/note-model'

export interface AttachOptions {
  /** Ask the sync to run now ("Sincronizar" in the notes list). */
  refresh?: () => void
}

export interface NotesState {
  notes: NoteMetadata[]
  isIndexLoading: boolean
  indexError: string | null

  currentNote: NoteContent | null
  isContentLoading: boolean
  contentError: string | null

  searchQuery: string
  searchResults: NoteMetadata[]
  searchTags: string[]
  searchDomains: string[]

  /** ms epoch of the last time the list was read from the repository. */
  lastSyncAt: number | null
  hasUnsavedChanges: boolean

  /** Design Lab: notes come from MockNotesRepository. */
  mockMode: boolean
}

export interface NotesActions {
  attach: (repository: NotesRepository, options?: AttachOptions) => void
  /** Forget the repository (services closed, device erased, tests). */
  detach: () => void
  enableMockMode: () => void

  loadIndex: () => Promise<void>
  /** Reload the list and ask the sync to fetch from Drive now. */
  rebuildIndex: () => Promise<void>
  refreshIndex: () => Promise<void>

  openNote: (id: string) => Promise<void>
  /** A note's body without touching `currentNote` (transclusion embeds). */
  getNoteContent: (id: string) => Promise<{ content: string; frontmatter: Record<string, unknown> } | null>
  closeNote: () => void
  saveNote: (content: string, frontmatter: Record<string, unknown>, id?: string) => Promise<void>
  /** Pass `parentId` (a Long Course's note id) to create a module of it. */
  createNote: (content: string, frontmatter: Record<string, unknown>, parentId?: string) => Promise<string>
  deleteNote: (id: string) => Promise<void>
  promoteToLongCourse: (id: string) => Promise<string>

  getBacklinks: (id: string) => Promise<Backlink[]>
  getUnlinkedMentions: (title: string, excludeId: string) => Promise<UnlinkedMention[]>

  setSearchQuery: (query: string) => void
  search: (query: string) => Promise<void>
  getTagCloud: () => Promise<void>
  getDomainCloud: () => Promise<void>

  setHasUnsavedChanges: (value: boolean) => void
  clearErrors: () => void
}

let repository: NotesRepository | null = null
let unsubscribe: (() => void) | null = null
let refreshHook: (() => void) | undefined

function requireRepository(): NotesRepository {
  if (!repository) throw new Error('Las notas todavía no están listas')
  return repository
}

/** Title (10) > domain (5) > tag (3). An empty query returns everything. */
export function searchNotes(notes: readonly NoteMetadata[], query: string): NoteMetadata[] {
  const q = query.toLowerCase().trim()
  if (!q) return [...notes]
  return notes
    .map((note) => {
      let score = 0
      if (note.title.toLowerCase().includes(q)) score += 10
      if (note.domain?.toLowerCase().includes(q)) score += 5
      if (note.tags.some((t) => t.toLowerCase().includes(q))) score += 3
      return { note, score }
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.note)
}

export const useNotesStore = create<NotesState & NotesActions>((set, get) => ({
  notes: [],
  isIndexLoading: false,
  indexError: null,

  currentNote: null,
  isContentLoading: false,
  contentError: null,

  searchQuery: '',
  searchResults: [],
  searchTags: [],
  searchDomains: [],

  lastSyncAt: null,
  hasUnsavedChanges: false,
  mockMode: false,

  attach: (repo, options = {}) => {
    // The Design Lab's mock wins over the real notes for the whole session.
    if (get().mockMode && !(repo instanceof MockNotesRepository)) return
    unsubscribe?.()
    repository = repo
    refreshHook = options.refresh
    unsubscribe = repo.subscribe(() => void get().loadIndex())
    set({ indexError: null })
    void get().loadIndex()
  },

  detach: () => {
    unsubscribe?.()
    unsubscribe = null
    repository = null
    refreshHook = undefined
    set({ notes: [], currentNote: null, mockMode: false, lastSyncAt: null })
  },

  enableMockMode: () => {
    if (get().mockMode) return
    set({ mockMode: true })
    get().attach(new MockNotesRepository())
  },

  // ── list ──────────────────────────────────────────────────────────────────

  loadIndex: async () => {
    if (!repository) return
    set({ isIndexLoading: true, indexError: null })
    try {
      const notes = await repository.list()
      set({ notes, isIndexLoading: false, lastSyncAt: Date.now() })
    } catch (err) {
      set({ indexError: err instanceof Error ? err.message : String(err), isIndexLoading: false })
      console.error('[NotesStore] Failed to read the notes:', err)
    }
  },

  rebuildIndex: async () => {
    refreshHook?.()
    await get().loadIndex()
  },

  refreshIndex: async () => get().rebuildIndex(),

  // ── one note ──────────────────────────────────────────────────────────────

  openNote: async (id) => {
    set({ isContentLoading: true, contentError: null })
    try {
      const note = await requireRepository().read(id)
      if (!note) throw new Error('No se encontró la nota')
      set({ currentNote: note, isContentLoading: false })
    } catch (err) {
      set({ contentError: err instanceof Error ? err.message : String(err), isContentLoading: false })
    }
  },

  getNoteContent: async (id) => {
    const note = await repository?.read(id)
    return note ? { content: note.content, frontmatter: note.frontmatter } : null
  },

  closeNote: () => set({ currentNote: null, hasUnsavedChanges: false }),

  saveNote: async (content, frontmatter, id) => {
    // `id` lets a flush-on-navigate save the right note after currentNote moved on.
    const target = id ?? get().currentNote?.file_id
    if (!target) throw new Error('No hay ninguna nota abierta')
    try {
      await requireRepository().save(target, content, frontmatter)
      set({ hasUnsavedChanges: false })
    } catch (err) {
      set({ contentError: err instanceof Error ? err.message : String(err) })
      throw err
    }
  },

  createNote: async (content, frontmatter, parentId) => {
    const id = await requireRepository().create(content, frontmatter, { parentId })
    set({ hasUnsavedChanges: false })
    return id
  },

  deleteNote: async (id) => {
    const removed = await requireRepository().remove(id)
    set((s) => ({ currentNote: removed.includes(s.currentNote?.file_id ?? '') ? null : s.currentNote }))
  },

  promoteToLongCourse: async (id) => {
    const courseId = await requireRepository().promoteToLongCourse(id)
    set({ hasUnsavedChanges: false })
    return courseId
  },

  getBacklinks: async (id) => (repository ? repository.backlinks(id) : []),
  getUnlinkedMentions: async (title, excludeId) => (repository ? repository.unlinkedMentions(title, excludeId) : []),

  // ── search (local, over the list already in memory) ───────────────────────

  setSearchQuery: (query) => set({ searchQuery: query }),

  search: async (query) => {
    set({ searchResults: searchNotes(get().notes, query) })
  },

  getTagCloud: async () => {
    set({ searchTags: [...new Set(get().notes.flatMap((n) => n.tags))].sort() })
  },

  getDomainCloud: async () => {
    set({ searchDomains: [...new Set(get().notes.map((n) => n.domain).filter((d): d is string => !!d))].sort() })
  },

  setHasUnsavedChanges: (value) => set({ hasUnsavedChanges: value }),
  clearErrors: () => set({ indexError: null, contentError: null }),
}))
